import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button.js";
import { DataTable } from "@primereact/ui/datatable";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Select as MultiSelect } from "@primereact/ui/select";
import { Tag } from "@primereact/ui/tag";
import { ToggleSwitch as Toggle } from "@primereact/ui/toggleswitch";
import { useTranslation } from "react-i18next";
import { Field } from "../../components/ui/Field.js";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";

type DeliveryStatus = "pending" | "processing" | "delivered" | "failed";

interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  active: boolean;
  events: string[];
  lastDeliveryAt: string | null;
  lastError: string | null;
  failureCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  deliveryCount: number;
  secret?: string;
}

interface WebhookDelivery {
  id: string;
  eventName: string;
  status: DeliveryStatus;
  attemptCount: number;
  responseStatus: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

interface WebhookDraft {
  id?: string;
  name: string;
  url: string;
  secret: string;
  active: boolean;
  events: string[];
  version?: number;
}

const EMPTY_DRAFT: WebhookDraft = {
  name: "",
  url: "",
  secret: "",
  active: true,
  events: [],
};

function formatDate(value: string | null): string {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}

function statusSeverity(
  status: DeliveryStatus,
): "success" | "danger" | "warn" | "info" {
  if (status === "delivered") return "success";
  if (status === "failed") return "danger";
  if (status === "processing") return "info";
  return "warn";
}

export function WebhookSettingsPanel() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManage = has("automations.manage");
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WebhookDraft | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const endpoints = useQuery({
    queryKey: ["automations", "webhooks"],
    queryFn: () =>
      apiGet<{ data: WebhookEndpoint[] }>("/automations/webhooks").then(
        (response) => response.data,
      ),
  });
  const supportedEvents = useQuery({
    queryKey: ["automations", "webhook-events"],
    queryFn: () =>
      apiGet<{ data: string[] }>("/automations/webhooks/supported-events").then(
        (response) => response.data,
      ),
  });
  const selected = endpoints.data?.find(({ id }) => id === selectedId) ?? null;
  const deliveries = useQuery({
    queryKey: ["automations", "webhook-deliveries", selectedId],
    queryFn: () =>
      apiGet<{ data: WebhookDelivery[] }>(
        `/automations/webhooks/${selectedId}/deliveries`,
      ).then((response) => response.data),
    enabled: Boolean(selectedId),
  });

  const eventOptions = useMemo(
    () =>
      (supportedEvents.data ?? []).map((event) => ({
        id: event,
        label:
          event === "*"
            ? t("automations.allEvents", {
                defaultValue: "All audit events (*)",
              })
            : event,
      })),
    [supportedEvents.data, t],
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["automations", "webhooks"],
    });
  };
  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("No webhook endpoint selected");
      const payload = {
        name: draft.name,
        url: draft.url,
        active: draft.active,
        events: draft.events,
        ...(draft.id
          ? { version: draft.version }
          : draft.secret
            ? { secret: draft.secret }
            : {}),
      };
      return draft.id
        ? apiPatch<{ data: WebhookEndpoint }>(
            `/automations/webhooks/${draft.id}`,
            payload,
          )
        : apiPost<{ data: WebhookEndpoint }>("/automations/webhooks", payload);
    },
    onSuccess: async (response) => {
      setDraft(null);
      setSelectedId(response.data.id);
      if (response.data.secret) setRevealedSecret(response.data.secret);
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (endpoint: WebhookEndpoint) =>
      apiPatch<{ data: WebhookEndpoint }>(
        `/automations/webhooks/${endpoint.id}`,
        { active: !endpoint.active, version: endpoint.version },
      ),
    onSuccess: refresh,
  });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      apiPost<{ data: WebhookEndpoint }>(
        `/automations/webhooks/${id}/rotate-secret`,
        {},
      ),
    onSuccess: async (response) => {
      setRevealedSecret(response.data.secret ?? null);
      await refresh();
    },
  });
  const testDelivery = useMutation({
    mutationFn: (id: string) => apiPost(`/automations/webhooks/${id}/test`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["automations", "webhook-deliveries", selectedId],
      });
    },
  });
  const retry = useMutation({
    mutationFn: (deliveryId: string) =>
      apiPost(
        `/automations/webhooks/${selectedId}/deliveries/${deliveryId}/retry`,
        {},
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["automations", "webhook-deliveries", selectedId],
      });
    },
  });
  const mutationError =
    save.error ??
    toggle.error ??
    rotate.error ??
    testDelivery.error ??
    retry.error;

  if (endpoints.isLoading || supportedEvents.isLoading) return <LoadingPanel />;
  if (endpoints.error || supportedEvents.error)
    return <ErrorPanel error={endpoints.error ?? supportedEvents.error} />;

  return (
    <div className="space-y-5">
      <Section
        title={t("automations.webhooks", { defaultValue: "Audit webhooks" })}
        subtitle={t("automations.description", {
          defaultValue:
            "Send signed audit events to n8n or another HTTPS endpoint.",
        })}
        actions={
          canManage ? (
            <Button onClick={() => setDraft({ ...EMPTY_DRAFT })}>
              <i className="pi pi-plus" />
              {t("automations.add", { defaultValue: "Add webhook" })}
            </Button>
          ) : undefined
        }
      >
        {mutationError ? <MutationError error={mutationError} /> : null}
        {(endpoints.data ?? []).length === 0 ? (
          <p className="text-sm text-app-muted">
            {t("automations.empty", {
              defaultValue: "No webhook endpoints configured.",
            })}
          </p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {endpoints.data!.map((endpoint) => (
              <button
                key={endpoint.id}
                type="button"
                onClick={() => setSelectedId(endpoint.id)}
                className={`rounded-xl border p-4 text-left transition ${selectedId === endpoint.id ? "border-brand bg-brand-soft" : "border-app-border bg-app-surface hover:border-brand"}`}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block font-semibold text-app-text">
                      {endpoint.name}
                    </span>
                    <span className="block truncate text-sm text-app-muted">
                      {endpoint.url}
                    </span>
                  </span>
                  <Tag severity={endpoint.active ? "success" : "secondary"}>
                    {endpoint.active
                      ? t("common.active", { defaultValue: "Active" })
                      : t("common.inactive", { defaultValue: "Inactive" })}
                  </Tag>
                </span>
                <span className="mt-3 block text-xs text-app-muted">
                  {endpoint.events.join(", ")} · {endpoint.deliveryCount}{" "}
                  {t("automations.deliveries", { defaultValue: "deliveries" })}
                </span>
                {endpoint.lastError ? (
                  <span className="mt-2 block text-xs text-danger">
                    {endpoint.lastError}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </Section>

      {selected ? (
        <Section
          title={selected.name}
          subtitle={selected.url}
          actions={
            canManage ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  severity="secondary"
                  variant="outlined"
                  onClick={() =>
                    setDraft({
                      id: selected.id,
                      name: selected.name,
                      url: selected.url,
                      secret: "",
                      active: selected.active,
                      events: selected.events,
                      version: selected.version,
                    })
                  }
                >
                  {t("common.edit", { defaultValue: "Edit" })}
                </Button>
                <Button
                  severity="secondary"
                  variant="outlined"
                  loading={toggle.isPending || undefined}
                  onClick={() => toggle.mutate(selected)}
                >
                  {selected.active
                    ? t("automations.disable", { defaultValue: "Disable" })
                    : t("automations.enable", { defaultValue: "Enable" })}
                </Button>
                <Button
                  severity="secondary"
                  variant="outlined"
                  loading={testDelivery.isPending || undefined}
                  onClick={() => testDelivery.mutate(selected.id)}
                >
                  {t("automations.test", { defaultValue: "Send test" })}
                </Button>
                <Button
                  severity="secondary"
                  variant="outlined"
                  loading={rotate.isPending || undefined}
                  onClick={() => rotate.mutate(selected.id)}
                >
                  {t("automations.rotate", { defaultValue: "Rotate secret" })}
                </Button>
              </div>
            ) : undefined
          }
        >
          <dl className="mb-5 grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase text-app-muted">
                {t("automations.lastDelivery", {
                  defaultValue: "Last delivery",
                })}
              </dt>
              <dd>{formatDate(selected.lastDeliveryAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-app-muted">
                {t("automations.failures", { defaultValue: "Failures" })}
              </dt>
              <dd>{selected.failureCount}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-app-muted">
                {t("automations.events", { defaultValue: "Events" })}
              </dt>
              <dd className="break-words">{selected.events.join(", ")}</dd>
            </div>
          </dl>
          {deliveries.isLoading ? (
            <LoadingPanel />
          ) : deliveries.error ? (
            <ErrorPanel error={deliveries.error} />
          ) : (
            <DataTable.Root>
              <DataTable.TableContainer>
                <DataTable.Table>
                  <DataTable.THead>
                    <DataTable.Row>
                      <DataTable.THeadCell>
                        <DataTable.THeadTitle>
                          {t("automations.event", { defaultValue: "Event" })}
                        </DataTable.THeadTitle>
                      </DataTable.THeadCell>
                      <DataTable.THeadCell>
                        <DataTable.THeadTitle>
                          {t("common.status", { defaultValue: "Status" })}
                        </DataTable.THeadTitle>
                      </DataTable.THeadCell>
                      <DataTable.THeadCell>
                        <DataTable.THeadTitle>
                          {t("automations.attempts", {
                            defaultValue: "Attempts",
                          })}
                        </DataTable.THeadTitle>
                      </DataTable.THeadCell>
                      <DataTable.THeadCell>
                        <DataTable.THeadTitle>
                          {t("automations.response", {
                            defaultValue: "Response",
                          })}
                        </DataTable.THeadTitle>
                      </DataTable.THeadCell>
                      <DataTable.THeadCell>
                        <DataTable.THeadTitle>
                          {t("automations.created")}
                        </DataTable.THeadTitle>
                      </DataTable.THeadCell>
                      <DataTable.THeadCell />
                    </DataTable.Row>
                  </DataTable.THead>
                  <DataTable.TBody>
                    {(deliveries.data ?? []).map((delivery) => (
                      <DataTable.Row key={delivery.id}>
                        <DataTable.Cell>{delivery.eventName}</DataTable.Cell>
                        <DataTable.Cell>
                          <Tag severity={statusSeverity(delivery.status)}>
                            {t(`automations.status.${delivery.status}`)}
                          </Tag>
                        </DataTable.Cell>
                        <DataTable.Cell>{delivery.attemptCount}</DataTable.Cell>
                        <DataTable.Cell>
                          <span
                            className={delivery.lastError ? "text-danger" : ""}
                          >
                            {delivery.responseStatus ??
                              delivery.lastError ??
                              "—"}
                          </span>
                        </DataTable.Cell>
                        <DataTable.Cell>
                          {formatDate(delivery.createdAt)}
                        </DataTable.Cell>
                        <DataTable.Cell>
                          {canManage && delivery.status === "failed" ? (
                            <Button
                              size="small"
                              variant="text"
                              loading={retry.isPending || undefined}
                              onClick={() => retry.mutate(delivery.id)}
                            >
                              {t("automations.retry")}
                            </Button>
                          ) : null}
                        </DataTable.Cell>
                      </DataTable.Row>
                    ))}
                  </DataTable.TBody>
                </DataTable.Table>
              </DataTable.TableContainer>
            </DataTable.Root>
          )}
        </Section>
      ) : null}

      <Modal.Root
        open={draft !== null}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && !save.isPending && setDraft(null)
        }
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {draft?.id
                    ? t("automations.edit", { defaultValue: "Edit webhook" })
                    : t("automations.add", { defaultValue: "Add webhook" })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times" />
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                {draft ? (
                  <form
                    className="space-y-4"
                    onSubmit={(event: FormEvent) => {
                      event.preventDefault();
                      save.mutate();
                    }}
                  >
                    <Field
                      label={t("common.name", { defaultValue: "Name" })}
                      required
                    >
                      <Input
                        value={draft.name}
                        required
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setDraft({ ...draft, name: event.target.value })
                        }
                      />
                    </Field>
                    <Field
                      label={t("automations.url", {
                        defaultValue: "Endpoint URL",
                      })}
                      required
                    >
                      <Input
                        type="url"
                        value={draft.url}
                        required
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setDraft({ ...draft, url: event.target.value })
                        }
                      />
                    </Field>
                    {!draft.id ? (
                      <Field
                        label={t("automations.secret", {
                          defaultValue: "Signing secret (optional)",
                        })}
                        hint={t("automations.secretHint", {
                          defaultValue:
                            "Leave empty to generate a strong secret.",
                        })}
                      >
                        <Input
                          type="password"
                          minLength={16}
                          value={draft.secret}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setDraft({ ...draft, secret: event.target.value })
                          }
                        />
                      </Field>
                    ) : null}
                    <Field
                      label={t("automations.events", {
                        defaultValue: "Events",
                      })}
                      required
                    >
                      <MultiSelect.Root
                        multiple
                        filter
                        fluid
                        value={draft.events}
                        options={eventOptions}
                        optionLabel="label"
                        optionValue="id"
                        onValueChange={(event: { value: unknown }) => {
                          const events = (event.value as string[]) ?? [];
                          setDraft({
                            ...draft,
                            events: events.includes("*") ? ["*"] : events,
                          });
                        }}
                      >
                        <MultiSelect.Trigger>
                          <MultiSelect.Value
                            placeholder={t("automations.selectEvents", {
                              defaultValue: "Select audit events",
                            })}
                          />
                          <MultiSelect.Indicator className="pi pi-chevron-down" />
                        </MultiSelect.Trigger>
                        <MultiSelect.Portal>
                          <MultiSelect.Positioner>
                            <MultiSelect.Popup>
                              <MultiSelect.Filter />
                              <MultiSelect.List />
                            </MultiSelect.Popup>
                          </MultiSelect.Positioner>
                        </MultiSelect.Portal>
                      </MultiSelect.Root>
                    </Field>
                    <Field
                      label={t("common.active", { defaultValue: "Active" })}
                    >
                      <Toggle.Root
                        checked={draft.active}
                        onCheckedChange={(event: { checked: boolean }) =>
                          setDraft({ ...draft, active: event.checked })
                        }
                      >
                        <Toggle.Control>
                          <Toggle.Handle />
                        </Toggle.Control>
                      </Toggle.Root>
                    </Field>
                    <MutationError error={save.error} />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        severity="secondary"
                        variant="outlined"
                        onClick={() => setDraft(null)}
                      >
                        {t("common.cancel", { defaultValue: "Cancel" })}
                      </Button>
                      <Button
                        type="submit"
                        loading={save.isPending || undefined}
                        disabled={draft.events.length === 0}
                      >
                        {t("common.save", { defaultValue: "Save" })}
                      </Button>
                    </div>
                  </form>
                ) : null}
              </Modal.Content>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      <Modal.Root
        open={revealedSecret !== null}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && setRevealedSecret(null)
        }
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("automations.saveSecret", {
                    defaultValue: "Save the signing secret",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times" />
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <p className="mb-3 text-sm text-app-muted">
                  {t("automations.secretOnce", {
                    defaultValue:
                      "This secret is shown once. Store it in n8n before closing.",
                  })}
                </p>
                <Input
                  readOnly
                  value={revealedSecret ?? ""}
                  onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                    event.currentTarget.select()
                  }
                />
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setRevealedSecret(null)}>
                    {t("automations.done")}
                  </Button>
                </div>
              </Modal.Content>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}
